<script lang="ts">
	import './userRoles.component.css';
	import { onMount } from 'svelte';
	import { UserService } from '../../../../domain-services/User.service';
	import type { UserRights } from '../../../../interfaces/UserRights';
	import { UserRoleEnum } from '../../../../interfaces/UserRoleEnum';
	import { clickOutside } from '../../../../utils/Dom.utils';
	import { VpBadge } from '@vtmn-play/svelte/VpBadge';
	import { VpIcon } from '@vtmn-play/svelte/VpIcon';
	import Chip from '../../../../__components__/chip/Chip.component.svelte';

	import { _ } from '../../../../utility-services/Translation.service';

	let dropDownElement: Element = $state();
	let isDropdownOpen = $state(false);
	let roles: UserRoleEnum[] = $state([]);

	const HOVER_CLOSE_DELAY_MS = 180;
	let hoverCloseTimeout: ReturnType<typeof setTimeout> | undefined;

	onMount(async () => {
		const userService = new UserService();
		try {
			const data: UserRights[] = (await userService.getUserRights()) ?? [];
			roles = data.map((role) => role.category as UserRoleEnum);
		} catch (error) {
			console.error('Error while fetching roles', error);
		}
	});

	function clearHoverCloseTimeout() {
		if (hoverCloseTimeout) {
			clearTimeout(hoverCloseTimeout);
			hoverCloseTimeout = undefined;
		}
	}

	function openDropdown() {
		clearHoverCloseTimeout();
		dropDownElement?.setAttribute('open', 'true');
		isDropdownOpen = true;
	}

	function closeDropdown() {
		clearHoverCloseTimeout();
		dropDownElement?.removeAttribute('open');
		isDropdownOpen = false;
	}

	function scheduleDropdownClose() {
		clearHoverCloseTimeout();
		hoverCloseTimeout = setTimeout(() => {
			closeDropdown();
		}, HOVER_CLOSE_DELAY_MS);
	}

	function closeSwitcher(node: HTMLElement) {
		clickOutside(node);
	}

	function handleClickOutside() {
		closeDropdown();
	}

	function handleMouseEnter() {
		if (roles.length !== 1) {
			openDropdown();
		}
	}

	function handleMouseLeave() {
		if (roles.length !== 1) {
			scheduleDropdownClose();
		}
	}

	function getRoleConfig(role: UserRoleEnum) {
		switch (role) {
			case UserRoleEnum.Admin:
				return {
					labelKey: 'role_admin',
					bgClass: 'chip-bg-admin',
					colorClass: 'chip-color-admin',
					icon: 'star-half-filled'
				};
			case UserRoleEnum.Communicator:
				return {
					labelKey: 'role_communicator',
					bgClass: 'chip-bg-communicator',
					colorClass: 'chip-color-communicator',
					icon: 'message'
				};
			case UserRoleEnum.SiteManager:
				return {
					labelKey: 'role_site_manager',
					bgClass: 'chip-bg-site-manager',
					colorClass: 'chip-color-site-manager',
					icon: 'circularity-maintain-repair'
				};
			case UserRoleEnum.Support:
				return {
					labelKey: 'role_support',
					bgClass: 'chip-bg-support',
					colorClass: 'chip-color-support',
					icon: 'customer-service'
				};
			case UserRoleEnum.Teammate:
				return {
					labelKey: 'role_teammate',
					bgClass: 'chip-bg-teammate',
					colorClass: 'chip-color-teammate',
					icon: 'group'
				};
			default:
				return {
					labelKey: role,
					bgClass: 'chip-bg-teammate',
					colorClass: 'chip-color-teammate',
					icon: 'user'
				};
		}
	}
</script>

<div class="block">
	{#if roles.length === 1}
		{@const config = getRoleConfig(roles[0])}
		<div class="vtmn-flex vtmn-items-center">
			<Chip
				variant="single-choice"
				size="medium"
				iconLeft={config.icon}
				iconLeftSize={18}
				class={config.bgClass}
				iconLeftClass={config.colorClass}
			>
				<span class="vtmn-typo_text-2 vtmn-font-bold vtmn-ml-2 {config.colorClass}">
					{$_(config.labelKey)}
				</span>
			</Chip>
		</div>
	{:else}
		<div class="vtmn-dropdown dropdown" use:closeSwitcher onclick_outside={handleClickOutside}>
			<details
				bind:this={dropDownElement}
				onmouseenter={handleMouseEnter}
				onmouseleave={handleMouseLeave}
				ontoggle={() => (isDropdownOpen = dropDownElement?.hasAttribute('open') ?? false)}
			>
				<summary
					class="userRoles_summary"
					onmouseenter={handleMouseEnter}
					onmouseleave={handleMouseLeave}
				>
					<div class="vtmn-flex vtmn-items-center vtmn-gap-2">
						<span>{$_('roles')}</span>
						<VpBadge variant="accent" size="small">{roles.length}</VpBadge>
						<VpIcon name={isDropdownOpen ? 'arrow-up-s-line' : 'arrow-down-s-line'} size={16} />
					</div>
				</summary>

				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<div
					class="vtmn-dropdown_items dropdownItemsRoles"
					onmouseenter={handleMouseEnter}
					onmouseleave={handleMouseLeave}
				>
					<div class="vtmn-flex vtmn-justify-between vtmn-items-center vtmn-mb-4">
						<span class="vtmn-typo_title-4 vtmn-font-bold">
							{$_('assigned_access')}
						</span>
						<VpBadge variant="accent" size="small">{roles.length}</VpBadge>
					</div>

					<div class="vtmn-flex vtmn-flex-col vtmn-gap-2 vtmn-items-start">
						{#if roles.length > 0}
							{#each roles as role}
								{@const config = getRoleConfig(role)}

								<Chip
									variant="single-choice"
									size="medium"
									iconLeft={config.icon}
									iconLeftSize={18}
									class={config.bgClass}
									iconLeftClass={config.colorClass}
								>
									<span class="vtmn-typo_text-2 vtmn-font-bold vtmn-ml-2 {config.colorClass}">
										{$_(config.labelKey)}
									</span>
								</Chip>
							{/each}
						{:else}
							<span class="vtmn-typo_text-3 vtmn-text-content-tertiary"
								>{$_('no_role_assigned')}</span
							>
						{/if}
					</div>
				</div>
			</details>
		</div>
	{/if}
</div>
